import { css, Global } from "@emotion/react";
import styled from "@emotion/styled";

import { theme } from "./theme";

// ── Global styles ──────────────────────────────────────────────

export const globalStyles = css`
  @import url("https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

  /* ── Dark palette (default) · Luik: 🟥 #da121a  🟨 #fcdd09 ── */
  :root {
    --c-bg: oklch(0.17 0.015 27);
    --c-bg-card: oklch(0.22 0.02 27);
    --c-bg-card-hover: oklch(0.26 0.025 27);
    --c-bg-input: oklch(0.26 0.02 27);
    --c-border: oklch(1 0 0 / 10%);
    --c-border-focus: oklch(0.9 0.19 95 / 55%);
    --c-text: oklch(0.97 0.01 95);
    --c-text-muted: oklch(0.72 0.02 60);
    --c-text-dim: oklch(0.55 0.02 50);
    --c-primary: oklch(0.9 0.19 95);
    --c-primary-fg: oklch(0.2 0.04 95);
    --c-primary-hover: oklch(0.92 0.17 95);
    --c-accent: oklch(0.55 0.23 27);
    --c-accent-fg: oklch(0.97 0.01 95);
    --c-danger: oklch(0.55 0.23 27);
    --c-danger-hover: oklch(0.6 0.23 27);
    --c-success: oklch(0.74 0.18 150);
    --c-warning: oklch(0.9 0.19 95);
    --c-gold: oklch(0.9 0.19 95);
    --c-silver: oklch(0.85 0.01 60);
    --c-bronze: oklch(0.65 0.12 50);
    --c-neon: oklch(0.92 0.19 95);
    --c-court: oklch(0.2 0.025 27);
    --c-court-line: oklch(1 0 0 / 12%);
    --c-secondary: oklch(0.28 0.025 27);
    --c-secondary-hover: oklch(0.32 0.03 27);
    --c-muted: oklch(0.25 0.02 27);
    --c-team-a: oklch(0.55 0.23 27);
    --c-team-b: oklch(0.9 0.19 95);
    --c-team-c: oklch(0.78 0.16 220);
    --c-team-d: oklch(0.75 0.18 305);

    --shadow-card: 0 1px 0 0 oklch(1 0 0 / 6%) inset, 0 20px 40px -20px oklch(0 0 0 / 60%);
    --shadow-glow:
      0 0 0 1px color-mix(in oklab, oklch(0.9 0.19 95) 35%, transparent),
      0 12px 40px -10px color-mix(in oklab, oklch(0.9 0.19 95) 25%, transparent);

    --grad-floodlight:
      radial-gradient(
        ellipse at 20% -10%,
        color-mix(in oklab, oklch(0.55 0.23 27) 20%, transparent) 0%,
        transparent 55%
      ),
      radial-gradient(
        ellipse at 100% 10%,
        color-mix(in oklab, oklch(0.9 0.19 95) 14%, transparent) 0%,
        transparent 50%
      ),
      oklch(0.17 0.015 27);
    --grad-primary: linear-gradient(
      135deg,
      oklch(0.9 0.19 95),
      color-mix(in oklab, oklch(0.9 0.19 95) 55%, oklch(0.55 0.23 27))
    );
    --grad-court: linear-gradient(
      180deg,
      oklch(0.2 0.025 27),
      color-mix(in oklab, oklch(0.2 0.025 27) 70%, oklch(0.17 0.015 27))
    );
  }

  /* ── Light palette · Luik ───────────────────────────────── */
  @media (prefers-color-scheme: light) {
    :root {
      --c-bg: oklch(0.97 0.006 95);
      --c-bg-card: oklch(1 0 0);
      --c-bg-card-hover: oklch(0.96 0.008 95);
      --c-bg-input: oklch(0.95 0.006 95);
      --c-border: oklch(0 0 0 / 10%);
      --c-border-focus: oklch(0.55 0.23 27 / 45%);
      --c-text: oklch(0.18 0.015 27);
      --c-text-muted: oklch(0.45 0.015 35);
      --c-text-dim: oklch(0.6 0.015 40);
      --c-primary: oklch(0.55 0.23 27);
      --c-primary-fg: oklch(0.97 0.01 95);
      --c-primary-hover: oklch(0.5 0.24 27);
      --c-accent: oklch(0.72 0.17 95);
      --c-accent-fg: oklch(0.2 0.04 95);
      --c-danger: oklch(0.52 0.24 27);
      --c-danger-hover: oklch(0.48 0.24 27);
      --c-success: oklch(0.55 0.18 150);
      --c-warning: oklch(0.72 0.17 95);
      --c-gold: oklch(0.78 0.17 95);
      --c-silver: oklch(0.75 0.01 60);
      --c-bronze: oklch(0.58 0.12 50);
      --c-neon: oklch(0.55 0.23 27);
      --c-court: oklch(0.95 0.008 95);
      --c-court-line: oklch(0 0 0 / 8%);
      --c-secondary: oklch(0.93 0.006 95);
      --c-secondary-hover: oklch(0.9 0.008 90);
      --c-muted: oklch(0.94 0.006 95);
      --c-team-a: oklch(0.5 0.23 27);
      --c-team-b: oklch(0.72 0.17 95);
      --c-team-c: oklch(0.6 0.16 220);
      --c-team-d: oklch(0.6 0.18 305);

      --shadow-card: 0 1px 3px oklch(0 0 0 / 8%), 0 4px 12px oklch(0 0 0 / 5%);
      --shadow-glow:
        0 0 0 1px color-mix(in oklab, oklch(0.55 0.23 27) 25%, transparent),
        0 8px 24px -6px color-mix(in oklab, oklch(0.55 0.23 27) 15%, transparent);

      --grad-floodlight:
        radial-gradient(
          ellipse at 20% -10%,
          color-mix(in oklab, oklch(0.55 0.23 27) 7%, transparent) 0%,
          transparent 55%
        ),
        radial-gradient(
          ellipse at 100% 10%,
          color-mix(in oklab, oklch(0.72 0.17 95) 6%, transparent) 0%,
          transparent 50%
        ),
        oklch(0.97 0.006 95);
      --grad-primary: linear-gradient(
        135deg,
        oklch(0.55 0.23 27),
        color-mix(in oklab, oklch(0.55 0.23 27) 55%, oklch(0.72 0.17 95))
      );
      --grad-court: linear-gradient(
        180deg,
        oklch(0.95 0.008 95),
        color-mix(in oklab, oklch(0.95 0.008 95) 70%, oklch(0.97 0.006 95))
      );
    }
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    color-scheme: light dark;
    -webkit-text-size-adjust: 100%;
    overflow: hidden;
    height: 100%;
  }

  body {
    background-color: ${theme.colors.bg};
    background: ${theme.gradients.floodlight};
    background-attachment: fixed;
    color: ${theme.colors.text};
    font-family: ${theme.fonts.body};
    line-height: 1.6;
    overflow: hidden;
    height: 100%;
    overscroll-behavior: none;
    -webkit-tap-highlight-color: transparent;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    padding-top: env(safe-area-inset-top, 0);
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);
    padding-bottom: env(safe-area-inset-bottom, 0);
  }

  h1,
  h2,
  h3 {
    font-family: ${theme.fonts.display};
    letter-spacing: -0.02em;
    font-weight: 700;
  }

  button,
  [role="button"] {
    -webkit-user-select: none;
    user-select: none;
  }
`;

export { Global };

// ── Layout ─────────────────────────────────────────────────────

export const AppContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

export const AppTitle = styled.h1`
  text-align: center;
  font-size: 1.5rem;
  margin-bottom: 12px;
  letter-spacing: -0.02em;

  ${theme.bp.md} {
    font-size: 2rem;
    margin-bottom: 32px;
  }
`;

// ── Section ────────────────────────────────────────────────────

export const Section = styled.section`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: 14px;
  margin-bottom: 16px;

  ${theme.bp.sm} {
    padding: 20px;
    margin-bottom: 20px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 1.1rem;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  ${theme.bp.sm} {
    font-size: 1.25rem;
    margin-bottom: 16px;
  }
`;

// ── Forms ──────────────────────────────────────────────────────

export const FormRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const inputStyles = css`
  background: ${theme.colors.bgInput};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.text};
  padding: 10px 12px;
  font-size: 1rem;
  min-height: ${theme.touch};
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.borderFocus};
  }

  ${theme.bp.md} {
    padding: 8px 12px;
    font-size: 0.875rem;
    min-height: unset;
  }
`;

export const TextInput = styled.input`
  ${inputStyles}
  min-width: 0;
  flex: 1;
`;

export const NumberInput = styled.input`
  ${inputStyles}
  width: 72px;
  flex: none;
`;

export const Select = styled.select`
  ${inputStyles}
`;

// ── Buttons ────────────────────────────────────────────────────

export const Button = styled.button<{
  variant?: "primary" | "danger" | "ghost";
  size?: "small" | "normal";
}>`
  background: ${(p) =>
    p.variant === "danger"
      ? theme.colors.danger
      : p.variant === "ghost"
        ? "transparent"
        : theme.colors.primary};
  color: ${(p) =>
    p.variant === "ghost"
      ? theme.colors.textMuted
      : p.variant === "danger"
        ? theme.colors.accentFg
        : theme.colors.primaryFg};
  border: ${(p) => (p.variant === "ghost" ? `1px solid ${theme.colors.border}` : "none")};
  border-radius: ${theme.radii.sm};
  /* Mobile: generous tap targets */
  padding: ${(p) => (p.size === "small" ? "8px 14px" : "12px 20px")};
  font-size: ${(p) => (p.size === "small" ? "0.85rem" : "1rem")};
  min-height: ${(p) => (p.size === "small" ? "40px" : theme.touch)};
  cursor: pointer;
  transition:
    background 0.2s,
    opacity 0.2s;
  font-weight: 500;
  white-space: nowrap;

  ${theme.bp.md} {
    padding: ${(p) => (p.size === "small" ? "4px 10px" : "8px 16px")};
    font-size: ${(p) => (p.size === "small" ? "0.75rem" : "0.875rem")};
    min-height: unset;
  }

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.variant === "danger"
        ? theme.colors.dangerHover
        : p.variant === "ghost"
          ? theme.colors.bgCardHover
          : theme.colors.primaryHover};
    color: ${(p) => (p.variant === "ghost" ? theme.colors.text : undefined)};
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ── Lists ──────────────────────────────────────────────────────

export const ItemList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ItemRow = styled.li`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  min-height: ${theme.touch};
  background: ${theme.colors.bg};
  border-radius: ${theme.radii.sm};
  border: 1px solid ${theme.colors.border};

  ${theme.bp.md} {
    padding: 8px 12px;
    min-height: unset;
    gap: 10px;
  }
`;

export const ItemName = styled.span`
  font-weight: 500;
  flex: 1;
  min-width: 0;
`;

export const ItemStat = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 0.85rem;
  white-space: nowrap;

  ${theme.bp.md} {
    font-size: 0.8rem;
  }
`;

export const ItemActions = styled.div`
  display: flex;
  gap: 4px;
  margin-left: auto;
`;

// ── Data bar ───────────────────────────────────────────────────

export const DataBar = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;

  ${theme.bp.md} {
    justify-content: flex-end;
    margin-bottom: 20px;
  }
`;

// ── Team grid ──────────────────────────────────────────────────

export const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin: 12px 0;

  ${theme.bp.sm} {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
  }
`;

export const TeamCard = styled.div<{ ranked?: boolean }>`
  border: 1px solid ${(p) => (p.ranked ? theme.colors.success : theme.colors.border)};
  border-radius: ${theme.radii.sm};
  padding: 12px;
  background: ${(p) => (p.ranked ? "rgba(34, 197, 94, 0.06)" : theme.colors.bg)};
  min-height: 80px;
  transition:
    border-color 0.15s,
    transform 0.1s;
`;

export const TeamCardTitle = styled.h3`
  font-size: 0.9rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const TeamCardList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TeamCardItem = styled.li`
  padding: 4px 8px;
  background: ${theme.colors.bgCard};
  border-radius: 4px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// ── Player chips / unassigned ──────────────────────────────────

export const UnassignedWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

export const PlayerChip = styled.span<{ dragging?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${theme.colors.bgInput};
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  font-size: 0.9rem;
  min-height: 36px;
  cursor: grab;
  -webkit-touch-callout: none;

  ${theme.bp.md} {
    padding: 4px 8px;
    font-size: 0.8rem;
    gap: 4px;
    min-height: unset;
  }

  &:active {
    cursor: grabbing;
  }
`;

export const UnassignedLabel = styled.h4`
  font-size: 0.9rem;
  margin-bottom: 8px;
  color: ${theme.colors.textMuted};
`;

// ── Drop target ────────────────────────────────────────────────

export const DropTarget = styled.div<{ isOver?: boolean }>`
  transition:
    background 0.15s,
    border-color 0.15s;
  border: 1px solid ${(p) => (p.isOver ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.isOver ? "rgba(99, 102, 241, 0.1)" : theme.colors.bg)};
  border-radius: ${theme.radii.sm};
  padding: 12px;
  min-height: 80px;
`;

// ── Leaderboard table ──────────────────────────────────────────

export const LeaderboardTableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -14px;
  padding: 0 14px;

  ${theme.bp.sm} {
    margin: 0;
    padding: 0;
  }
`;

export const LeaderboardTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  ${theme.bp.md} {
    font-size: 0.875rem;
  }

  thead th {
    text-align: left;
    padding: 10px 10px;
    border-bottom: 2px solid ${theme.colors.border};
    color: ${theme.colors.textMuted};
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;

    ${theme.bp.md} {
      padding: 8px 12px;
      font-size: 0.8rem;
    }
  }

  tbody tr {
    border-bottom: 1px solid ${theme.colors.border};
    transition: background 0.15s;

    &:hover {
      background: ${theme.colors.bgCardHover};
    }
  }

  td {
    padding: 10px;

    ${theme.bp.md} {
      padding: 10px 12px;
    }
  }
`;

export const RankBadge = styled.span<{ rank?: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.8rem;
  background: ${(p) =>
    p.rank === 1
      ? theme.colors.gold
      : p.rank === 2
        ? theme.colors.silver
        : p.rank === 3
          ? theme.colors.bronze
          : "transparent"};
  color: ${(p) => (p.rank === 1 || p.rank === 2 ? "#000" : p.rank === 3 ? "#fff" : "inherit")};
`;

// ── Match history ──────────────────────────────────────────────

export const MatchCard = styled.div`
  background: ${theme.colors.bg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  padding: 12px;
  margin-bottom: 10px;

  ${theme.bp.sm} {
    padding: 16px;
    margin-bottom: 12px;
  }
`;

export const MatchCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;

  h3 {
    font-size: 1rem;
    font-weight: 600;
  }
`;

export const Meta = styled.span`
  color: ${theme.colors.textDim};
  font-size: 0.8rem;
`;

export const TeamResultBlock = styled.div`
  margin-bottom: 8px;

  h4 {
    font-size: 0.85rem;
    color: ${theme.colors.textMuted};
    margin-bottom: 4px;
  }

  ul {
    list-style: none;
  }

  li {
    padding: 6px 0;
    border-bottom: 1px solid ${theme.colors.border};
    font-size: 0.85rem;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: baseline;

    &:last-child {
      border-bottom: none;
    }
  }
`;

export const DeltaPositive = styled.span`
  color: ${theme.colors.success};
`;

export const DeltaNegative = styled.span`
  color: ${theme.colors.danger};
`;

// ── Chart container ────────────────────────────────────────────

export const ChartContainer = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: ${theme.colors.bg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};

  h4 {
    font-size: 0.85rem;
    color: ${theme.colors.textMuted};
    margin-bottom: 8px;
  }
`;

export const BreakdownList = styled.span`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};

  ${theme.bp.md} {
    font-size: 0.75rem;
  }
`;

// ── Error text ─────────────────────────────────────────────────

export const ErrorText = styled.p`
  color: ${theme.colors.danger};
  font-size: 0.8rem;
  margin-top: 4px;
`;

// ── Result phase ───────────────────────────────────────────────

export const ResultInstructions = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 0.85rem;
  margin-bottom: 12px;

  strong {
    color: ${theme.colors.text};
  }
`;

export const TeamCardButton = styled.button`
  all: unset;
  cursor: pointer;
  display: block;
  width: 100%;
  -webkit-touch-callout: none;

  & > div {
    transition:
      border-color 0.15s,
      transform 0.1s;
  }

  &:hover > div {
    border-color: ${theme.colors.primary};
  }

  &:active > div {
    transform: scale(0.98);
  }
`;

// ── Simulation ─────────────────────────────────────────────────

export const SimConfigGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 12px;

  ${theme.bp.md} {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
`;

export const SimConfigCol = styled.div`
  h3 {
    font-size: 0.95rem;
    margin-bottom: 10px;
    color: ${theme.colors.textMuted};
  }
`;

export const TunerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

export const TunerLabel = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.8rem;
  color: ${theme.colors.textDim};
  gap: 4px;

  ${theme.bp.md} {
    font-size: 0.75rem;
  }

  input[type="number"] {
    width: 100%;
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: ${theme.colors.text};
  grid-column: span 2;
  min-height: ${theme.touch};

  ${theme.bp.md} {
    font-size: 0.8rem;
    gap: 6px;
    min-height: unset;
  }

  input[type="checkbox"] {
    accent-color: ${theme.colors.primary};
    width: 20px;
    height: 20px;

    ${theme.bp.md} {
      width: 16px;
      height: 16px;
    }
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 16px;

  ${theme.bp.sm} {
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 10px;
    margin-bottom: 20px;
  }
`;

export const StatCardEl = styled.div`
  background: ${theme.colors.bg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  padding: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StatCardValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

export const StatCardLabel = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.textDim};
  text-transform: uppercase;
  letter-spacing: 0.03em;

  ${theme.bp.md} {
    font-size: 0.7rem;
  }
`;

export const Charts2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;

  ${theme.bp.md} {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
`;

export const SimResults = styled.div`
  border-top: 1px solid ${theme.colors.border};
  padding-top: 16px;
  margin-top: 16px;
`;

// ── Tabs ───────────────────────────────────────────────────────

export const TabBar = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.border};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Tab = styled.button<{ active?: boolean }>`
  background: ${(p) => (p.active ? theme.colors.bgCard : "transparent")};
  color: ${(p) => (p.active ? theme.colors.text : theme.colors.textDim)};
  border: none;
  border-bottom: 2px solid ${(p) => (p.active ? theme.colors.primary : "transparent")};
  padding: 10px 16px;
  font-size: 0.9rem;
  min-height: ${theme.touch};
  cursor: pointer;
  margin-bottom: -2px;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  ${theme.bp.md} {
    padding: 8px 16px;
    font-size: 0.85rem;
    min-height: unset;
  }

  &:hover {
    color: ${theme.colors.text};
    background: ${theme.colors.bgCardHover};
  }
`;

// ── Sweep table ────────────────────────────────────────────────

export const SweepTableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -14px;
  padding: 0 14px;

  ${theme.bp.sm} {
    margin: 0;
    padding: 0;
  }
`;

export const SweepTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;

  th,
  td {
    padding: 8px 10px;
    border: 1px solid ${theme.colors.border};
    text-align: center;
    white-space: nowrap;

    ${theme.bp.md} {
      padding: 6px 10px;
    }
  }

  th {
    background: ${theme.colors.bgInput};
    color: ${theme.colors.textMuted};
    font-weight: 600;
  }

  tbody tr:hover {
    background: ${theme.colors.bgCardHover};
  }
`;

export const HeatCell = styled.td<{ intensity: number }>`
  background: ${(p) => {
    const g = Math.round(p.intensity * 180 + 40);
    return `rgba(${34 + (1 - p.intensity) * 80}, ${g}, ${80 - p.intensity * 40}, 0.35)`;
  }};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

// ── Floodlight glass primitives ────────────────────────────────

export const GlassCard = styled.div`
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgCard} 70%, transparent);
  box-shadow: ${theme.shadow.card};
  backdrop-filter: blur(8px);
  padding: 1rem;

  & + & {
    margin-top: 0.75rem;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

export const CardTitle = styled.h2`
  font-family: ${theme.fonts.display};
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const CardSubtitle = styled.p`
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};
  margin-bottom: 0.75rem;
`;

export const FloodStageTag = styled.p`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${theme.colors.primary};
  margin-bottom: 0.25rem;
`;

export const PillBtn = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: ${theme.radii.full};
  background: ${theme.colors.secondary};
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${theme.colors.text};
  transition: background 0.12s;

  &:hover {
    background: ${theme.colors.secondaryHover};
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const PillBtnPrimary = styled(PillBtn)`
  background: ${theme.gradients.primary};
  color: ${theme.colors.primaryFg};
  box-shadow: ${theme.shadow.glow};

  &:hover {
    filter: brightness(1.05);
    background: ${theme.gradients.primary};
  }
`;

export const PillBtnDanger = styled(PillBtn)`
  background: color-mix(in oklab, ${theme.colors.danger} 15%, transparent);
  color: ${theme.colors.danger};

  &:hover {
    background: color-mix(in oklab, ${theme.colors.danger} 25%, transparent);
  }
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
`;

export const GlassInput = styled.input`
  all: unset;
  width: 100%;
  padding: 0.625rem 0.875rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgInput} 70%, transparent);
  font-size: 0.875rem;
  color: ${theme.colors.text};
  transition: border-color 0.15s;
  min-height: 2.75rem;
  box-sizing: border-box;

  &::placeholder {
    color: ${theme.colors.textDim};
  }

  &:focus {
    border-color: ${theme.colors.borderFocus};
  }
`;

export const GlassSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgInput} 70%, transparent);
  font-size: 0.8125rem;
  color: ${theme.colors.text};
  min-height: 2.5rem;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.borderFocus};
  }
`;

export const GlassNumberInput = styled.input`
  all: unset;
  width: 4rem;
  padding: 0.375rem 0.625rem;
  border-radius: ${theme.radii.lg};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgInput} 70%, transparent);
  font-size: 0.8125rem;
  color: ${theme.colors.text};
  text-align: center;
  font-variant-numeric: tabular-nums;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.borderFocus};
  }
`;

export const ItemChip = styled.button<{ active?: boolean }>`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: ${theme.radii.full};
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid
    ${(p) =>
      p.active
        ? `color-mix(in oklab, ${theme.colors.primary} 50%, transparent)`
        : theme.colors.border};
  background: ${(p) =>
    p.active
      ? `color-mix(in oklab, ${theme.colors.primary} 12%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bg} 60%, transparent)`};
  color: ${(p) => (p.active ? theme.colors.primary : theme.colors.text)};
  transition: all 0.12s;

  &:hover {
    border-color: color-mix(in oklab, ${theme.colors.primary} 40%, transparent);
  }
`;

export const ListGap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const GlassRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bg} 60%, transparent);
  min-height: 2.75rem;
`;

export const RowName = styled.span`
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMeta = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  white-space: nowrap;
`;

export const RowActions = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
  flex-shrink: 0;
`;

export const SmallIconBtn = styled.button`
  all: unset;
  cursor: pointer;
  width: 1.75rem;
  height: 1.75rem;
  display: grid;
  place-items: center;
  border-radius: ${theme.radii.lg};
  font-size: 0.8125rem;
  transition: background 0.12s;

  &:hover {
    background: ${theme.colors.secondaryHover};
  }

  &:active {
    transform: scale(0.92);
  }
`;

export const FloodEmptyState = styled.div`
  text-align: center;
  color: ${theme.colors.textMuted};
  padding: 2rem 1rem;
  font-size: 0.875rem;
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.radii["2xl"]};
`;

export const ToggleRow = styled.button<{ on?: boolean }>`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid
    ${(p) =>
      p.on ? `color-mix(in oklab, ${theme.colors.primary} 40%, transparent)` : theme.colors.border};
  background: ${(p) =>
    p.on
      ? `color-mix(in oklab, ${theme.colors.primary} 8%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bg} 60%, transparent)`};
  transition: all 0.15s;
  text-align: left;

  &:active {
    transform: scale(0.99);
  }
`;

export const ToggleDot = styled.span<{ on?: boolean }>`
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
  border-radius: ${theme.radii.full};
  border: 2px solid ${(p) => (p.on ? theme.colors.primary : theme.colors.textDim)};
  display: grid;
  place-items: center;
  font-size: 0.625rem;
  color: ${theme.colors.primaryFg};
  background: ${(p) => (p.on ? theme.colors.primary : "transparent")};
  transition: all 0.15s;
`;

export const ToggleBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ToggleLabel = styled.span<{ on?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(p) => (p.on ? theme.colors.text : theme.colors.textDim)};
  display: block;
`;

export const ToggleDesc = styled.span`
  font-size: 0.7rem;
  color: ${theme.colors.textDim};
  display: block;
  margin-top: 0.125rem;
`;

export const ParamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.375rem;
`;

export const ParamLabel = styled.label`
  font-size: 0.7rem;
  color: ${theme.colors.textDim};
  flex: 1;
  min-width: 5rem;
`;

export const ParamHint = styled.p`
  font-size: 0.625rem;
  color: ${theme.colors.textMuted};
  font-style: italic;
  margin-top: 0.125rem;
  line-height: 1.3;
`;

// ── Segmented control ──────────────────────────────────────────

export const SegmentStrip = styled.div`
  display: flex;
  gap: 2px;
  padding: 3px;
  border-radius: ${theme.radii["2xl"]};
  background: color-mix(in oklab, ${theme.colors.bg} 60%, transparent);
  border: 1px solid ${theme.colors.border};
  min-width: 0;
  overflow: hidden;
`;

export const Segment = styled.button<{ active?: boolean }>`
  all: unset;
  cursor: pointer;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.25rem;
  border-radius: ${theme.radii.xl};
  font-size: 0.6875rem;
  font-weight: 500;
  text-align: center;
  transition: all 0.15s;
  min-height: 2.25rem;

  color: ${(p) => (p.active ? theme.colors.primaryFg : theme.colors.textMuted)};
  background: ${(p) => (p.active ? theme.gradients.primary : "transparent")};
  box-shadow: ${(p) => (p.active ? theme.shadow.glow : "none")};

  &:hover {
    color: ${(p) => (p.active ? theme.colors.primaryFg : theme.colors.text)};
  }

  &:active {
    transform: scale(0.97);
  }
`;

// ── Format mode cards ──────────────────────────────────────────

export const ModeCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

export const ModeCard = styled.button<{ active?: boolean }>`
  all: unset;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0.875rem 0.5rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid
    ${(p) =>
      p.active
        ? `color-mix(in oklab, ${theme.colors.primary} 50%, transparent)`
        : theme.colors.border};
  background: ${(p) =>
    p.active
      ? `color-mix(in oklab, ${theme.colors.primary} 10%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bg} 50%, transparent)`};
  transition: all 0.15s;
  text-align: center;
  min-height: ${theme.touch};

  ${(p) => p.active && `box-shadow: ${theme.shadow.glow};`}

  &:hover {
    border-color: color-mix(in oklab, ${theme.colors.primary} 40%, transparent);
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const ModeCardIcon = styled.span`
  font-size: 1.5rem;
  line-height: 1;
`;

export const ModeCardLabel = styled.span<{ active?: boolean }>`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) => (p.active ? theme.colors.primary : theme.colors.textMuted)};
  transition: color 0.15s;
`;

// ── Field grouping ─────────────────────────────────────────────

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-left: 0.625rem;
  border-left: 2px solid color-mix(in oklab, ${theme.colors.primary} 30%, transparent);
`;

export const FieldGroupLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${theme.colors.primary};
`;

// ── Hero name input ────────────────────────────────────────────

export const HeroInput = styled.input`
  all: unset;
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgInput} 70%, transparent);
  font-family: ${theme.fonts.display};
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text};
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  box-sizing: border-box;

  &::placeholder {
    color: ${theme.colors.textDim};
    font-weight: 400;
  }

  &:focus {
    border-color: ${theme.colors.borderFocus};
    box-shadow: ${theme.shadow.glow};
  }
`;

// ── Roster number ──────────────────────────────────────────────

export const RosterNumber = styled.span`
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: ${theme.radii.full};
  background: ${theme.gradients.primary};
  color: ${theme.colors.primaryFg};
  font-size: 0.6875rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  box-shadow: ${theme.shadow.glow};
`;

// ── Stat pill badges ───────────────────────────────────────────

export const StatPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: ${theme.radii.full};
  font-size: 0.6875rem;
  font-weight: 500;
  background: color-mix(in oklab, ${theme.colors.bg} 60%, transparent);
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
`;

// ── Chip toggle all ────────────────────────────────────────────

export const ChipToggleAll = styled.button<{ allSelected?: boolean }>`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: ${theme.radii.full};
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px dashed
    ${(p) =>
      p.allSelected
        ? `color-mix(in oklab, ${theme.colors.primary} 50%, transparent)`
        : theme.colors.border};
  background: ${(p) =>
    p.allSelected ? `color-mix(in oklab, ${theme.colors.primary} 8%, transparent)` : "transparent"};
  color: ${(p) => (p.allSelected ? theme.colors.primary : theme.colors.textDim)};
  transition: all 0.12s;

  &:hover {
    border-color: color-mix(in oklab, ${theme.colors.primary} 40%, transparent);
    color: ${theme.colors.text};
  }

  &:active {
    transform: scale(0.96);
  }
`;

// ── Two-column field layout ────────────────────────────────────

export const FieldColumns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

// ── Subtle inline divider ──────────────────────────────────────

export const GlassDivider = styled.hr`
  border: none;
  height: 1px;
  background: ${theme.colors.border};
  margin: 0.5rem 0;
`;

// ── Confirm-delete row state ───────────────────────────────────

export const ConfirmDeleteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid color-mix(in oklab, ${theme.colors.danger} 40%, transparent);
  background: color-mix(in oklab, ${theme.colors.danger} 8%, transparent);
  min-height: 2.75rem;
  animation: confirmPulse 0.3s ease-out;

  @keyframes confirmPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.01);
    }
    100% {
      transform: scale(1);
    }
  }
`;

export const ConfirmDeleteText = styled.span`
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${theme.colors.danger};
`;

// ── Mini toggle (inline checkbox alternative) ──────────────────

export const MiniToggle = styled.button<{ on?: boolean }>`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: ${theme.radii.full};
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid
    ${(p) =>
      p.on ? `color-mix(in oklab, ${theme.colors.primary} 40%, transparent)` : theme.colors.border};
  background: ${(p) =>
    p.on
      ? `color-mix(in oklab, ${theme.colors.primary} 10%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bg} 60%, transparent)`};
  color: ${(p) => (p.on ? theme.colors.primary : theme.colors.textMuted)};
  transition: all 0.12s;

  &:active {
    transform: scale(0.97);
  }
`;

export const MiniToggleDot = styled.span<{ on?: boolean }>`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: ${theme.radii.full};
  border: 1.5px solid ${(p) => (p.on ? theme.colors.primary : theme.colors.textDim)};
  background: ${(p) => (p.on ? theme.colors.primary : "transparent")};
  transition: all 0.15s;
`;

// ── Card count badge ───────────────────────────────────────────

export const CountBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${theme.colors.primary};
`;
